import { getHttp } from '@speechki-frontend/shared/axios/httpRef';

import { makeid } from '@speechki-frontend/shared/helpers/functions';

import i18n from '@speechki-frontend/core/i18n/i18n';

import { isRtlLanguage } from '@speechki-frontend/core/editor/utils';

import { fetchBook, fetchBookContents } from '../api/books';

/* @TODO
    [ ] replace axios plugin calls with api instance
    [ ] make namespaced module

*/

const COUNTRY_NAMES = {
    russia: 'Российская федерация',
    ukraine: 'Украина',
    usa: 'USA',
};

const state = {
    bookLanguages: [],
    book: {},
    categories: [],
    ageRatings: [],
    contents: [],
};

const getters = {
    bookTitle: (state) => {
        if (state.book && state.book.name) return state.book.name;

        return '';
    },
    reviewers({ book: { reviewers = [] } }) {
        return reviewers;
    },
    ageRatingCountries: (state) => {
        if (!state.ageRatings.length) return [];

        return state.ageRatings
            .map((rating) => rating.country)
            .filter((value, index, array) => {
                return array.indexOf(value) === index;
            })
            .map((country) => ({
                id: country,
                name: COUNTRY_NAMES[country],
            }));
    },
    ageRatingsByCountry: (state) => {
        if (!state.ageRatings.length) return {};

        const options = {};

        state.ageRatings.forEach((item) => {
            if (!options[item.country]) {
                options[item.country] = [];
            }
            options[item.country].push(item);
        });

        return options;
    },
    isBookTrial: ({ book }) => {
        if (!book.id) return false;

        return !book.limit || (book.limit_info && book.limit_info.is_trial);
    },
    bookLanguage: ({ book }) => {
        if (!book.id) return '';

        return book.book_language;
    },
    bookMp3: ({ book }) => {
        if (!book || !book.builds || !book.builds.length) return null;

        let mp3 = book.builds.find((build) => build.build_type === 'mp3');

        if (!mp3 || !mp3.document) return null;

        return mp3.document;
    },
    speakerNonFreeLimit({ book }, getters, rootState) {
        const currency = rootState.auth.user.currency;
        const speaker = book.speaker.id;
        const limits = rootState.payment.limits.filter(
            (lim) =>
                lim.currency === currency &&
                lim.name !== 'free' &&
                lim.params.calculate_rule === 'symbols'
        );
        return limits.find((lim) => {
            return lim.speakers.includes((sp) => sp.id === speaker);
        });
    },
    bookApproximateDuration({ book }) {
        if (!book.speaker || !book.speaker.duration_ratio) return 0;

        return (book.speaker.duration_ratio * book.symbols) / 0.96 / book.speed;
    },
    bookRtl({ book }) {
        const { book_language: lang = '' } = book;
        return isRtlLanguage(lang);
    },
    isReviewer: (_, getters, rootState, rootGetters) => {
        if (rootGetters['isGuestAccess']) {
            return rootGetters['guestAccess/isInGroup']('reviewer');
        }

        return getters.reviewers.includes(rootState.auth.user.pk);
    },
    arrayBookLanguages(state) {
        return Object.entries(state.bookLanguages).map(([key, value]) => ({
            id: key,
            name: value,
        }));
    },
    authorName({ book }) {
        if (!book.authors || !book.authors.length) return 'Unknown author';

        return book.authors.map((author) => `${author.first_name} ${author.last_name}`).join(', ');
    },
    bookSize({ book }) {
        const k = book.symbols / 1000;
        if (k < 1) {
            return book.symbols + i18n.global.t('payment.symbols');
        }
        return Math.round(book.symbols / 1000) + i18n.global.t('bookEdit.size');
    },
    bookId({ book }) {
        return book.id;
    },
};

const mutations = {
    SET_BOOK(state, book) {
        state.book = book;
    },
    SET_BOOK_LANGUAGES(state, langs) {
        state.bookLanguages = langs;
    },
    SET_CATEGORIES(state, categories) {
        state.categories = categories;
    },
    SET_AGE_RATINGS(state, ratings) {
        state.ageRatings = ratings;
    },
    SET_BOOK_BUILD_MODIFIED(state) {
        state.book.is_modified_build = true;
    },
    CLEAR_BOOK(state) {
        state.book = {};
    },
    SET_CONTENTS(state, contents) {
        state.contents = contents;
    },
};

const actions = {
    async fetchBook({ commit }, id) {
        const book = await fetchBook(id);
        commit('SET_BOOK', book);
    },
    async fetchCategories({ commit }) {
        const { results } = await getHttp().get('/api/v1/categories/?page_size=200');
        commit('SET_CATEGORIES', results);
    },
    async fetchBookLanguages({ commit }) {
        const langs = await getHttp().get('/api/v2/books/book_languages/');
        commit('SET_BOOK_LANGUAGES', langs);
    },
    async fetchAgeRatings({ commit }) {
        const { results } = await getHttp().get('/api/v1/age_ratings/?page_size=100');
        commit('SET_AGE_RATINGS', results);
    },
    clearBook({ commit }) {
        commit('CLEAR_BOOK');
    },
    setBookModifiedBuild({ commit }) {
        commit('SET_BOOK_BUILD_MODIFIED');
    },
    async fetchContents({ commit }, bookid) {
        try {
            const contents = await fetchBookContents(bookid);
            commit('SET_CONTENTS', contents);
        } catch (e) {
            console.log(e);
        }
    },
};

export default {
    state,
    getters,
    mutations,
    actions,
};
